import { Fragment, type ReactNode } from "react";
import { useThemeContext } from "../../state/themeState/themeContext";

/**
 * Remounts the screen subtree when the colour scheme (or custom theme) changes.
 *
 * Colours come from the `ColoursObj` global, which `setColourScheme` mutates in place
 * (same object reference). NativeNavigationRouter caches each native view's React
 * element with its `children` frozen, so a theme change never propagates into
 * already-created views top-down. Components that read `ColoursObj` directly (not via
 * the MUI theme context) keep stale colours in every view except the one the user is
 * actively re-rendering. This gate subscribes to the theme via zustand, which
 * re-renders it regardless of the frozen parent, and re-keys the subtree so every open
 * view re-reads `ColoursObj`. Theme changes are rare user actions, so the remount cost
 * (screen effects re-running) is acceptable.
 *
 * On web / Android, components that read `ColoursObj` into inline styles keep the colours
 * of their last render too, so the gate applies on every platform.
 *
 * Remounting discards screen state: scroll position, expanded rows, un-synced form input.
 */
export function ThemeRemountGate({ children }: { children: ReactNode }) {
  const scheme = useThemeContext((s) => s.selectedColourScheme);
  const customThemeId = useThemeContext((s) => s.customThemeId);

  return <Fragment key={`${scheme}:${customThemeId}`}>{children}</Fragment>;
}
